export const MUX_HEADER_SIZE = 25;
export const MUX_DEFAULT_MAX_PAYLOAD_BYTES = 16 * 1024 * 1024;
export const MUX_MAGIC = 0x00535752;
export const MUX_ANY_PEER_IDX = 0xffff;
export const MUX_NO_PLUGIN_ID = 0xffff;
export const MUX_CORE_PLUGIN_ID = 0x0000;
export const MUX_MAX_U16 = 0xffff;
export const MUX_MAX_U32 = 0xffffffff;

export enum MuxChannel {
  Control = 0x0000,
  Video = 0x0001,
  Input = 0x0002,
  Audio = 0x0003,
  File = 0x0004,
  PluginData = 0x0005,
  PluginControl = 0x0006,
  Rdp = 0x0007,
  Chat = 0x0008,
  Metrics = 0x0009,
  Upgrade = 0x000a,
}

export enum MuxFlags {
  None = 0,
  Keyframe = 1 << 0,
  Config = 1 << 1,
  Fin = 1 << 2,
  Err = 1 << 3,
  Compressed = 1 << 4,
  Encrypted = 1 << 5,
}

export enum MuxRoutingScope {
  UnicastPeer = 0x00,
  OperatorBroadcast = 0x01,
  AllOperators = 0x02,
  AllPeers = 0x03,
}

export const MUX_KNOWN_CHANNELS = [
  MuxChannel.Control,
  MuxChannel.Video,
  MuxChannel.Input,
  MuxChannel.Audio,
  MuxChannel.File,
  MuxChannel.PluginData,
  MuxChannel.PluginControl,
  MuxChannel.Rdp,
  MuxChannel.Chat,
  MuxChannel.Metrics,
  MuxChannel.Upgrade,
] as const;

export const MUX_BROWSER_JSON_READY_CHANNELS = ["CONTROL", "PLUGIN_DATA", "PLUGIN_CONTROL", "CHAT", "METRICS"] as const;
export const MUX_BROWSER_DATA_READY_CHANNELS = ["VIDEO", "INPUT", "FILE", "RDP"] as const;

const MUX_KNOWN_CHANNEL_SET = new Set<number>(MUX_KNOWN_CHANNELS);
const MUX_KNOWN_ROUTING_SCOPES = new Set<number>([
  MuxRoutingScope.UnicastPeer,
  MuxRoutingScope.OperatorBroadcast,
  MuxRoutingScope.AllOperators,
  MuxRoutingScope.AllPeers,
]);

export interface MuxHeader {
  payloadLength: number;
  channel: MuxChannel;
  flags: number;
  fromPeerIdx: number;
  toPeerIdx: number;
  streamId: number;
  pluginId: number;
  routingScope: MuxRoutingScope;
  targetOperatorId: number;
}

export type MuxHeaderInput = Partial<Omit<MuxHeader, "payloadLength" | "channel">> & {
  channel: MuxChannel;
};

export interface MuxFrame {
  header: MuxHeader;
  payload: ArrayBuffer;
}

export interface MuxDecodeOptions {
  maxPayloadBytes?: number;
}

export interface MuxEncodeOptions {
  maxPayloadBytes?: number;
}

export interface StreamIdAllocatorOptions {
  first?: number;
  maxStreamId?: number;
  maxActive?: number;
}

const MUX_KNOWN_FLAG_MASK =
  MuxFlags.Keyframe | MuxFlags.Config | MuxFlags.Fin | MuxFlags.Err | MuxFlags.Compressed | MuxFlags.Encrypted;
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export class MuxProtocolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MuxProtocolError";
  }
}

export class StreamIdAllocator {
  private readonly active = new Set<number>();
  private next: number;
  readonly maxStreamId: number;
  readonly maxActive: number;

  constructor(options: StreamIdAllocatorOptions = {}) {
    this.maxStreamId = normalizePositiveUint32(options.maxStreamId ?? MUX_MAX_U32, "maxStreamId");
    this.maxActive = normalizePositiveUint32(options.maxActive ?? 4096, "maxActive");
    if (this.maxActive > this.maxStreamId) {
      throw new RangeError("maxActive must be <= maxStreamId");
    }
    this.next = normalizePositiveUint32(options.first ?? 1, "first");
    if (this.next > this.maxStreamId) this.next = 1;
  }

  allocate(): number {
    if (this.active.size >= this.maxActive) {
      throw new Error("mux_stream_limit");
    }
    let candidate = this.next;
    for (let scanned = 0; scanned < this.maxStreamId; scanned++) {
      if (candidate < 1 || candidate > this.maxStreamId) candidate = 1;
      if (!this.active.has(candidate)) {
        this.active.add(candidate);
        this.next = candidate >= this.maxStreamId ? 1 : candidate + 1;
        return candidate;
      }
      candidate = candidate >= this.maxStreamId ? 1 : candidate + 1;
    }
    throw new Error("mux_stream_exhausted");
  }

  release(streamId: number): void {
    if (Number.isInteger(streamId)) this.active.delete(streamId >>> 0);
  }

  isActive(streamId: number): boolean {
    return Number.isInteger(streamId) && this.active.has(streamId >>> 0);
  }

  get activeCount(): number {
    return this.active.size;
  }
}

export function isMuxFrame(data: ArrayBuffer | ArrayBufferView): boolean {
  const bytes = asUint8Array(data);
  return bytes.byteLength >= 4 && bytes[0] === 0x52 && bytes[1] === 0x57 && bytes[2] === 0x53 && bytes[3] === 0x00;
}

export function encodeMuxFrame(
  header: MuxHeaderInput,
  payload: ArrayBuffer | ArrayBufferView = new Uint8Array(0),
  options: MuxEncodeOptions = {},
): ArrayBuffer {
  const payloadBytes = asUint8Array(payload);
  const maxPayloadBytes = options.maxPayloadBytes ?? MUX_DEFAULT_MAX_PAYLOAD_BYTES;
  validatePayloadLength(payloadBytes.byteLength, maxPayloadBytes);

  const normalized = normalizeHeaderInput(header, payloadBytes.byteLength);
  const frame = new Uint8Array(MUX_HEADER_SIZE + payloadBytes.byteLength);
  const view = new DataView(frame.buffer);
  view.setUint32(0, MUX_MAGIC, true);
  view.setUint32(4, normalized.payloadLength, true);
  view.setUint16(8, normalized.channel, true);
  view.setUint16(10, normalized.flags, true);
  view.setUint16(12, normalized.fromPeerIdx, true);
  view.setUint16(14, normalized.toPeerIdx, true);
  view.setUint32(16, normalized.streamId, true);
  view.setUint16(20, normalized.pluginId, true);
  view.setUint8(22, normalized.routingScope);
  view.setUint16(23, normalized.targetOperatorId, true);
  frame.set(payloadBytes, MUX_HEADER_SIZE);
  return frame.buffer;
}

export function decodeMuxFrame(data: ArrayBuffer | ArrayBufferView, options: MuxDecodeOptions = {}): MuxFrame {
  const bytes = asUint8Array(data);
  const maxPayloadBytes = options.maxPayloadBytes ?? MUX_DEFAULT_MAX_PAYLOAD_BYTES;
  if (bytes.byteLength < MUX_HEADER_SIZE) {
    throw new MuxProtocolError("mux_frame_too_short");
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const magic = view.getUint32(0, true);
  if (magic !== MUX_MAGIC) {
    throw new MuxProtocolError("mux_bad_magic");
  }
  const payloadLength = view.getUint32(4, true);
  validatePayloadLength(payloadLength, maxPayloadBytes);
  if (bytes.byteLength !== MUX_HEADER_SIZE + payloadLength) {
    throw new MuxProtocolError("mux_bad_length");
  }

  const channel = view.getUint16(8, true);
  const flags = view.getUint16(10, true);
  const routingScope = view.getUint8(22);
  validateKnownChannel(channel);
  validateFlags(flags);
  validateRoutingScope(routingScope);

  return {
    header: {
      payloadLength,
      channel,
      flags,
      fromPeerIdx: view.getUint16(12, true),
      toPeerIdx: view.getUint16(14, true),
      streamId: view.getUint32(16, true),
      pluginId: view.getUint16(20, true),
      routingScope,
      targetOperatorId: view.getUint16(23, true),
    },
    payload: copyToArrayBuffer(bytes.subarray(MUX_HEADER_SIZE, MUX_HEADER_SIZE + payloadLength)),
  };
}

export function encodeMuxJsonFrame(
  header: MuxHeaderInput,
  value: unknown,
  options: MuxEncodeOptions = {},
): ArrayBuffer {
  return encodeMuxFrame(header, encodeMuxJsonPayload(value), options);
}

export function encodeMuxJsonPayload(value: unknown): Uint8Array {
  return textEncoder.encode(JSON.stringify(value));
}

export function decodeMuxJsonPayload<T = unknown>(payload: ArrayBuffer | ArrayBufferView): T {
  return JSON.parse(textDecoder.decode(asUint8Array(payload))) as T;
}

export function decodeMuxJsonFrame<T = unknown>(
  data: ArrayBuffer | ArrayBufferView,
  options: MuxDecodeOptions = {},
): {
  frame: MuxFrame;
  value: T;
} {
  const frame = decodeMuxFrame(data, options);
  return { frame, value: decodeMuxJsonPayload<T>(frame.payload) };
}

export function muxStableStreamId(value: string): number {
  return fnv1a32NonZero(textEncoder.encode(value));
}

export function muxStableStreamIdBytes(bytes: Uint8Array): number {
  return fnv1a32NonZero(bytes);
}

export function rdpStreamIdFromTunnelId(tunnelId: string): number | null {
  const bytes = rdpGuidBytes(tunnelId);
  return bytes ? fnv1a32NonZero(bytes) : null;
}

export function rdpStreamIdFromFrame(data: ArrayBuffer | ArrayBufferView): number {
  const bytes = asUint8Array(data);
  if (bytes.byteLength >= 18 && bytes[0] === 0x52 && bytes[1] === 0x44) {
    return fnv1a32NonZero(bytes.subarray(2, 18));
  }
  return 1;
}

export function isMuxBrowserEnabled(): boolean {
  return readMuxFlag(
    ["RWS_MUX_BROWSER", "VITE_RWS_MUX_BROWSER"],
    ["__remotews_mux_browser__", "RWS_MUX_BROWSER"],
    ["muxBrowser", "rwsMuxBrowser", "RWS_MUX_BROWSER"],
  );
}

export function isMuxToolsEnabled(): boolean {
  return true;
}

export function isMuxDataPlaneEnabled(): boolean {
  return true;
}

export function muxBrowserReadyChannels(): string[] {
  const channels: string[] = [...MUX_BROWSER_JSON_READY_CHANNELS];
  if (isMuxDataPlaneEnabled()) channels.push(...MUX_BROWSER_DATA_READY_CHANNELS);
  return channels;
}

function normalizeHeaderInput(header: MuxHeaderInput, payloadLength: number): MuxHeader {
  const normalized: MuxHeader = {
    payloadLength,
    channel: header.channel,
    flags: header.flags ?? MuxFlags.None,
    fromPeerIdx: header.fromPeerIdx ?? MUX_ANY_PEER_IDX,
    toPeerIdx: header.toPeerIdx ?? MUX_ANY_PEER_IDX,
    streamId: header.streamId ?? 0,
    pluginId: header.pluginId ?? MUX_NO_PLUGIN_ID,
    routingScope: header.routingScope ?? MuxRoutingScope.UnicastPeer,
    targetOperatorId: header.targetOperatorId ?? 0,
  };
  validateKnownChannel(normalized.channel);
  validateFlags(normalized.flags);
  validateU16(normalized.fromPeerIdx, "fromPeerIdx");
  validateU16(normalized.toPeerIdx, "toPeerIdx");
  validateU32(normalized.streamId, "streamId");
  validateU16(normalized.pluginId, "pluginId");
  validateRoutingScope(normalized.routingScope);
  validateU16(normalized.targetOperatorId, "targetOperatorId");
  return normalized;
}

function validatePayloadLength(length: number, maxPayloadBytes: number): void {
  validateU32(length, "payloadLength");
  validateU32(maxPayloadBytes, "maxPayloadBytes");
  if (length > maxPayloadBytes) {
    throw new MuxProtocolError("mux_payload_too_large");
  }
}

function validateKnownChannel(channel: number): asserts channel is MuxChannel {
  validateU16(channel, "channel");
  if (!MUX_KNOWN_CHANNEL_SET.has(channel)) {
    throw new MuxProtocolError("mux_unknown_channel");
  }
}

function validateFlags(flags: number): void {
  validateU16(flags, "flags");
  if ((flags & ~MUX_KNOWN_FLAG_MASK) !== 0) {
    throw new MuxProtocolError("mux_reserved_flags");
  }
}

function validateRoutingScope(scope: number): asserts scope is MuxRoutingScope {
  validateU16(scope, "routingScope");
  if (!MUX_KNOWN_ROUTING_SCOPES.has(scope)) {
    throw new MuxProtocolError("mux_unknown_routing_scope");
  }
}

function validateU16(value: number, name: string): void {
  if (!Number.isInteger(value) || value < 0 || value > MUX_MAX_U16) {
    throw new RangeError(`${name} must be uint16`);
  }
}

function validateU32(value: number, name: string): void {
  if (!Number.isInteger(value) || value < 0 || value > MUX_MAX_U32) {
    throw new RangeError(`${name} must be uint32`);
  }
}

function normalizePositiveUint32(value: number, name: string): number {
  validateU32(value, name);
  if (value < 1) throw new RangeError(`${name} must be >= 1`);
  return value;
}

function fnv1a32NonZero(bytes: Uint8Array): number {
  let hash = 0x811c9dc5;
  for (const b of bytes) {
    hash ^= b;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash === 0 ? 1 : hash;
}

function rdpGuidBytes(tunnelId: string): Uint8Array | null {
  const hex = tunnelId.replace(/-/g, "");
  if (!/^[0-9a-fA-F]{32}$/.test(hex)) return null;
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  swap(bytes, 0, 3);
  swap(bytes, 1, 2);
  swap(bytes, 4, 5);
  swap(bytes, 6, 7);
  return bytes;
}

function swap(bytes: Uint8Array, a: number, b: number): void {
  const tmp = bytes[a];
  bytes[a] = bytes[b];
  bytes[b] = tmp;
}

function asUint8Array(data: ArrayBuffer | ArrayBufferView): Uint8Array {
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
}

function copyToArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

function readMuxFlag(envKeys: string[], globalKeys: string[], configKeys: string[]): boolean {
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
  for (const key of envKeys) {
    if (flagValueEnabled(env?.[key])) return true;
  }
  const globals = globalThis as unknown as Record<string, unknown>;
  for (const key of globalKeys) {
    if (flagValueEnabled(globals[key])) return true;
  }
  const config = globals.__remotews_config__;
  if (config && typeof config === "object") {
    const cfg = config as Record<string, unknown>;
    for (const key of configKeys) {
      if (flagValueEnabled(cfg[key])) return true;
    }
  }
  return false;
}

function flagValueEnabled(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value !== "string") return false;
  const normalized = value.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "on" || normalized === "yes";
}
